import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    this.style.top = OFFSET + 'px';

    // The panel has no `left`/`right` of its own, so it resolves to its static
    // position — which is anchored to the *inline* start edge, i.e. the right edge
    // under RTL. Writing an inline `left` would discard that and re-anchor the
    // panel to the physical left of its container, throwing it across the trigger.
    // A transform nudges the panel without disturbing how it is anchored, so the
    // same code keeps the panel on-screen in both directions.
    //
    // Reset before measuring, otherwise a previous nudge is baked into the rect and
    // the offsets accumulate across resizes.
    this.style.transform = '';

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    let shift = 0;
    if (rect.right > viewportWidth) {
      shift = -(rect.right - viewportWidth + OFFSET);
    } else if (rect.left < 0) {
      shift = Math.abs(rect.left) + OFFSET;
    }

    if (shift !== 0) this.style.transform = `translateX(${shift}px)`;

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
